import React, { FC, useState } from 'react'
import { useToggleLayer, Arrow } from 'react-laag'
import cx from 'classnames'
import { Stack } from 'components/Stack'
import { useTranslation } from 'react-i18next'
import { useId } from '@reach/auto-id'
import { isValidHex, toHex, toHexString } from 'utils/color'
import styles from './ColorPicker.module.scss'

export const DEFAULT_COLORS = [
  '#37d67a',
  '#2bcde4',
  '#697689',
  '#f47373',
  '#c27ace',
  '#a34747',
  '#0080b6',
  '#555555',
  '#dce776',
  '#ff8a66',
  '#ffad5c',
  '#46c7d8',
  '#a816a8',
  '#5416a8',
  '#ff547b',
]

type ColorPickerProps = {
  id?: string
  value?: string
  colors?: string[]
  onChange: (color: string) => void
}

export const ColorPicker: FC<ColorPickerProps> = ({
  id,
  colors = DEFAULT_COLORS,
  onChange,
  value: color,
}) => {
  const { t } = useTranslation()
  const inputId = useId(id)
  const [value, setValue] = useState(toHex(color))

  const changeColor = (color: string) => {
    setValue(color)

    if (isValidHex(color)) {
      const newColor = toHexString(color)
      onChange(newColor)
    }
  }

  const updateInputValue = (color: string) => {
    setValue(toHex(color))
  }

  const [element, toggleLayerProps] = useToggleLayer(
    ({ isOpen, layerProps, arrowStyle, layerSide }) =>
      isOpen && (
        <div {...layerProps} className={styles.popup}>
          <Stack space="small">
            <div className={styles.input}>
              <label htmlFor={inputId}>{t('color_picker.label')}</label>
              <span>#</span>
              <input
                id={inputId}
                type="text"
                value={value}
                onChange={(event) => changeColor(event.target.value)}
                onBlur={(event) => updateInputValue(event.target.value)}
              />
            </div>

            <div className={styles.colors}>
              {colors.map((color: string) => (
                <button
                  key={color}
                  style={{ backgroundColor: color }}
                  onClick={() => changeColor(toHex(color))}
                  aria-label={t('color_picker.pick_color', { color })}
                />
              ))}
            </div>

            <div className={styles.colorSample} style={{ backgroundColor: color }}>
              #{toHex(color)}
            </div>
          </Stack>
          <Arrow
            style={arrowStyle}
            layerSide={layerSide}
            backgroundColor={styles.whiteColor}
            borderWidth={1}
            borderColor={styles.borderGrey}
          />
        </div>
      ),
    {
      placement: {
        anchor: 'TOP_CENTER',
        autoAdjust: true,
        triggerOffset: 20,
      },
      fixed: true,
      closeOnOutsideClick: true,
    },
  )

  return (
    <>
      {element}
      <button
        className={cx({ [styles.trigger]: true, [styles.active]: toggleLayerProps.isOpen })}
        onClick={toggleLayerProps.openFromContextMenuEvent}
        aria-label={t('color_picker.select_color')}
      >
        <span style={{ backgroundColor: color }} />
      </button>
    </>
  )
}

ColorPicker.displayName = 'ColorPicker'
